// utils
import { PublicConfig } from "@/shared/PublicConfig";

// components
import PolicySummary from "@/shared/components/PolicySummary";
import { Heading } from "@/shared/components/ui/Heading";
import { CheckoutSteps } from "./CheckoutSteps";

// hooks
import { useStateScopedBreeds } from "@/shared/hooks/useStateScopedBreeds";

// types
import { Quote } from "@/shared/types/Quote.interface";
import { FormStepProps } from "@/shared/types/Form";
import { PetUnderwriterType } from "@spotpetinsurance/spot-types/entities/PetQuote";

type BillingEditorProps = FormStepProps<Quote>;

/**
 * Canada Checkout step editor.
 *
 * Two columns: the coverage summary and the checkout steps (Terms & Conditions →
 * Payment, with billing frequency). The Stripe-coupled PTZ-US BillingEditor is not reused.
 */
export function BillingEditor(props: BillingEditorProps) {
    const quote = props?.value as Quote;

    // Province-scoped breeds for the coverage summary. The postal isn't a stateCode — pass the
    // persisted province (ratingAddress.state) so the correct CA list resolves.
    const { breeds } = useStateScopedBreeds({
        underwriter: PublicConfig.PTZ_CA.UNDERWRITER as PetUnderwriterType,
        stateCode: quote?.ratingAddress?.state || undefined
    });

    return (
        <div className="mx-auto flex w-full flex-col gap-7">
            {/* 2 Col layout */}
            <div className="flex flex-col gap-7 md:flex-row md:gap-8">
                {/* Coverage summary — Col 1 on mobile, Col 2 on desktop */}
                <div className="order-1 md:order-2 md:w-1/2">
                    <PolicySummary quote={quote} breeds={breeds} config={{ isSingleCol: true }} underwriter={PublicConfig.PTZ_CA.UNDERWRITER as PetUnderwriterType} />
                </div>

                {/* Checkout — Col 2 on mobile, Col 1 on desktop */}
                <div className="order-2 flex flex-col gap-5 md:order-1 md:w-1/2">
                    <Heading level="h1" className="text-lg font-bold">
                        Checkout
                    </Heading>
                    {props.value && <CheckoutSteps quote={props.value} />}
                </div>
            </div>
        </div>
    );
}
